/**
 * Prepare initial Market Appraisal inputs from property data, RP extraction results, and AI-generated content
 */

import type { MarketAppraisalInputs, ComparableSaleInput } from '../types/marketAppraisal';

interface PropertyData {
  address?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    formattedAddress?: string;
  };
  propertyType?: string;
  features?: {
    bedrooms?: number;
    bathrooms?: number;
    parking?: number;
    area?: number;
    totalFloorArea?: number;
    yearBuilt?: number;
  };
  priceRange?: {
    min?: number;
    max?: number;
  };
  owner?: {
    name?: string;
    email?: string;
  } | Array<{
    name?: string;
    email?: string;
  }>;
}

interface AgencyData {
  name?: string;
  logoUrl?: string;
  theme?: any;
}

interface AgentData {
  firstName?: string;
  lastName?: string;
  licenceNumber?: string;
}

interface ExtractedData {
  comparables?: any[];
  marketStatistics?: {
    medianPrice?: string | null;
    movement12m?: string | null;
    avgDom?: string | null;
    clearanceRate?: string | null;
  };
}

interface ReportContent {
  content?: string;
}

/**
 * Format address for display
 */
function formatAddress(address?: PropertyData['address']): string {
  if (!address) return '—';
  if (address.formattedAddress) return address.formattedAddress;
  const parts = [
    address.street,
    address.city,
    address.state,
    address.postalCode
  ].filter(Boolean);
  return parts.join(', ') || '—';
}

/**
 * Format currency
 */
function formatCurrency(amount: number | string | null | undefined): string {
  if (amount === null || amount === undefined) return '';
  const num = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.]/g, '')) : amount;
  if (isNaN(num)) return '';
  return `$${num.toLocaleString()}`;
}

/**
 * Get primary owner name
 */
function getPrimaryOwnerName(owner?: PropertyData['owner']): string {
  if (!owner) return 'Property Owner';
  if (Array.isArray(owner)) {
    return owner[0]?.name || 'Property Owner';
  }
  return owner?.name || 'Property Owner';
}

/**
 * Parse AI-generated report content to extract structured data
 * This is a basic parser - can be enhanced with more sophisticated extraction
 */
function parseReportContent(content?: string): {
  pricing?: { rangeLow?: string; rangeHigh?: string; confidence?: string };
  market?: { demand?: string; stock?: string; momentum?: string; summary?: string; buyerBehaviour?: string; demandDrivers?: string[] };
  presentation?: { condition?: string; buyerAppeal?: string; score?: string; notes?: string };
  strategy?: { method?: string; rationale?: string; pricingApproach?: string; risks?: string[] };
  marketing?: { modules?: string[]; duration?: string; notes?: string };
  propertyFeatures?: string[];
  competition?: Array<{ address: string; priceGuide: string; dom: string | number; notes: string }>;
  competitionInsight?: string;
  nextSteps?: string[];
} {
  if (!content) return {};

  const result: any = {};

  // Extract price range (look for patterns like "$1,500,000 – $2,000,000")
  const priceRangeMatch = content.match(/\$([\d,]+)\s*[–-]\s*\$([\d,]+)/i);
  if (priceRangeMatch) {
    result.pricing = {
      rangeLow: `$${priceRangeMatch[1]}`,
      rangeHigh: `$${priceRangeMatch[2]}`,
      confidence: 'Moderate'
    };
  }

  // Extract market conditions (basic patterns)
  const demandMatch = content.match(/buyer\s+demand[:\s]+(\w+)/i);
  if (demandMatch) {
    result.market = result.market || {};
    result.market.demand = demandMatch[1].charAt(0).toUpperCase() + demandMatch[1].slice(1).toLowerCase();
  }

  // Extract presentation condition (look for common condition values)
  const conditionPatterns = [
    /(?:overall\s+)?condition[:\s]+(excellent|very\s+good|good|fair|poor|average|moderate|outstanding|superior)/i,
    /condition[:\s]+(excellent|very\s+good|good|fair|poor|average|moderate|outstanding|superior)/i,
    /(excellent|very\s+good|good|fair|poor|average|moderate|outstanding|superior)\s+condition/i
  ];
  
  for (const pattern of conditionPatterns) {
    const conditionMatch = content.match(pattern);
    if (conditionMatch) {
      result.presentation = result.presentation || {};
      const conditionValue = conditionMatch[1].replace(/\s+/g, ' ').trim(); // Normalize whitespace
      result.presentation.condition = conditionValue.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
      break; // Use first match found
    }
  }

  // Extract sale method
  const methodMatch = content.match(/(?:sale\s+method|recommended\s+method)[:\s]+([^.\n]+)/i);
  if (methodMatch) {
    result.strategy = result.strategy || {};
    result.strategy.method = methodMatch[1].trim();
  }

  return result;
}

/**
 * Convert extracted comparable sales to dialog format
 */
function convertComparables(comparables?: any[]): ComparableSaleInput[] {
  if (!comparables || !Array.isArray(comparables)) return [];
  
  return comparables.map(comp => ({
    address: comp.address || '—',
    saleStatus: comp.saleStatus || 'Sold',
    salePrice: comp.salePrice ? formatCurrency(comp.salePrice) : undefined,
    soldDate: comp.soldDate || '—',
    distanceKm: comp.distanceKm || '—',
    landSizeSqm: comp.landSizeSqm || undefined,
    internalSizeSqm: comp.internalSizeSqm || undefined,
    bedrooms: comp.bedrooms || '—',
    bathrooms: comp.bathrooms || '—',
    carSpaces: comp.carSpaces || '—',
    yearBuilt: comp.yearBuilt || undefined,
    dom: comp.dom || undefined,
    flags: comp.flags || undefined,
    firstListing: comp.firstListing || undefined,
    lastListing: comp.lastListing || undefined,
    notes: comp.notes || undefined,
    tags: comp.tags || undefined,
    selected: comp.selected !== undefined ? comp.selected : true // Default to selected if not specified
  }));
}

/**
 * Prepare initial inputs for the Market Appraisal confirmation dialog
 */
export function prepareAppraisalInputs(
  property: PropertyData,
  agency: AgencyData,
  agent: AgentData,
  extractedData?: ExtractedData,
  reportContent?: ReportContent
): MarketAppraisalInputs {
  const address = formatAddress(property.address);
  const primaryOwner = getPrimaryOwnerName(property.owner);
  const agentName = agent.firstName && agent.lastName
    ? `${agent.firstName} ${agent.lastName}`
    : agent.firstName || agent.lastName || 'Agent';

  // Parse report content for initial values
  const parsedContent = parseReportContent(reportContent?.content);

  // Format price range
  const priceRange = property.priceRange
    ? {
        rangeLow: formatCurrency(property.priceRange.min) || '(To be determined)',
        rangeHigh: formatCurrency(property.priceRange.max) || '(To be determined)',
        confidence: parsedContent.pricing?.confidence || 'Moderate'
      }
    : {
        rangeLow: parsedContent.pricing?.rangeLow || '(To be determined)',
        rangeHigh: parsedContent.pricing?.rangeHigh || '(To be determined)',
        confidence: parsedContent.pricing?.confidence || 'Low'
      };

  return {
    // Auto-filled
    property: {
      address,
      type: property.propertyType || 'Property',
      bedrooms: property.features?.bedrooms || '—',
      bathrooms: property.features?.bathrooms || '—',
      carSpaces: property.features?.parking || '—',
      landSizeSqm: property.features?.area,
      internalSizeSqm: property.features?.totalFloorArea,
      yearBuilt: property.features?.yearBuilt
    },
    agency: {
      name: agency.name || 'Agency'
    },
    agent: {
      name: agentName,
      licenceNo: agent.licenceNumber || '—'
    },
    vendor: {
      name: primaryOwner
    },
    appraisal: {
      date: new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }),
      version: '1.0'
    },

    // Extracted from RP data
    extractedData: {
      comparables: convertComparables(extractedData?.comparables),
      marketStatistics: {
        medianPrice: extractedData?.marketStatistics?.medianPrice || null,
        movement12m: extractedData?.marketStatistics?.movement12m || null,
        avgDom: extractedData?.marketStatistics?.avgDom || null,
        clearanceRate: extractedData?.marketStatistics?.clearanceRate || null
      }
    },

    // Manual inputs (with defaults from parsed content)
    manualInputs: {
      pricing: priceRange,
      market: {
        demand: parsedContent.market?.demand || 'Moderate',
        stock: 'Normal',
        momentum: 'Stable',
        summary: parsedContent.market?.summary || 'Market conditions are stable with moderate buyer interest.',
        buyerBehaviour: parsedContent.market?.buyerBehaviour || 'Buyers are taking time to make decisions, with a focus on value and location.',
        demandDrivers: parsedContent.market?.demandDrivers || [
          'Location proximity to amenities',
          'Property size and features',
          'Market timing'
        ]
      },
      presentation: {
        condition: parsedContent.presentation?.condition || 'Good',
        buyerAppeal: 'Moderate',
        score: '7/10',
        notes: parsedContent.presentation?.notes || 'Property presents well with good potential for buyer interest.'
      },
      propertyFeatures: parsedContent.propertyFeatures || [],
      competition: parsedContent.competition || [],
      competitionInsight: parsedContent.competitionInsight,
      strategy: {
        method: parsedContent.strategy?.method || 'Private Treaty',
        rationale: parsedContent.strategy?.rationale || 'Based on current market conditions and comparable sales.',
        pricingApproach: parsedContent.strategy?.pricingApproach || 'Market-based pricing',
        risks: parsedContent.strategy?.risks || [
          'Market conditions may change',
          'Buyer competition may vary'
        ]
      },
      marketing: {
        modules: parsedContent.marketing?.modules || [
          'Professional photography',
          'Online listings',
          'Open house events'
        ],
        duration: parsedContent.marketing?.duration || '8-12 weeks',
        notes: parsedContent.marketing?.notes || 'Standard marketing campaign recommended.'
      },
      agentStats: [
        { label: 'Years of experience', value: '10+' },
        { label: 'Properties sold', value: '50+' },
        { label: 'Average days on market', value: '35' }
      ],
      recentSales: [],
      nextSteps: parsedContent.nextSteps || [
        'Review and discuss appraisal with vendor',
        'Confirm pricing strategy',
        'Prepare marketing campaign',
        'Schedule photography and staging',
        'Launch property listing'
      ]
    }
  };
}
